import { useParams, Link } from 'react-router-dom';
import { useMemo, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const category = useMemo(() => {
    return categories.find((c) => c.slug === slug);
  }, [slug]);

  const filteredMovies = useMemo(() => {
    if (!slug || slug === 'all') return movies;
    return movies.filter((m) => m.categorySlug === slug);
  }, [slug]);

  const categoryName = slug === 'all' ? '全部影片' : category?.name || '未知分类';

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: categoryName },
  ];

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 91精品视频在线播放`,
    description: `浏览${categoryName}分类下的精选影片，高品质影视推荐，告别片荒。`,
    url: `https://91jingpin.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '91精品视频在线播放',
      url: 'https://91jingpin.com/',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mt-6 mb-8">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-500 mt-2">
                  共 {filteredMovies.length} 部影片
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-8">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-background-50 shadow-sm'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-movie-2-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无影片</p>
                  <Link
                    to="/"
                    className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600"
                  >
                    返回首页
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}